import { CommonModule } from '@angular/common';
import { Component, effect, inject, signal, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { SbcApiService } from '../services/sbc-api.service';
import { SessionService } from '../../../core/services/session.service';

import { PageBackButtonComponent } from '../../../shared/components/page-back-button/page-back-button.component';
import { PageRefreshButtonComponent } from '../../../shared/components/page-refresh-button/page-refresh-button.component';

@Component({
  selector: 'app-audit',
  standalone: true,
  imports: [CommonModule, FormsModule, PageRefreshButtonComponent, PageBackButtonComponent],
  templateUrl: './audit.component.html',
  styleUrl: './audit.component.css',
})
export class AuditComponent {
  private readonly api = inject(SbcApiService);
  readonly session = inject(SessionService);

  rows = signal<Record<string, unknown>[]>([]);
  error = signal('');
  loading = signal(false);
  entityType = '';

  page = signal(1);
  pageSize = signal(10);
  total = signal(0);
  totalPages = signal(1);

  constructor() {
    // Only re-fetch when authority changes. untracked() avoids page/pageSize
    // becoming effect deps (which was resetting Next back to page 1).
    effect(() => {
      this.session.authorityId();
      untracked(() => {
        this.page.set(1);
        this.reload();
      });
    });
  }

  reload(): void {
    this.loading.set(true);
    this.error.set('');
    const page = this.page();
    const pageSize = this.pageSize();
    this.api
      .listAuditEvents({
        entityType: this.entityType || undefined,
        page,
        pageSize,
      })
      .subscribe({
        next: (res) => {
          this.rows.set(res.data || []);
          this.total.set(Number(res.meta?.total ?? res.data?.length ?? 0));
          this.page.set(Number(res.meta?.page ?? page));
          this.pageSize.set(Number(res.meta?.pageSize ?? pageSize));
          this.totalPages.set(Number(res.meta?.totalPages ?? 1));
          this.loading.set(false);
        },
        error: (err) => {
          this.loading.set(false);
          this.error.set(err?.error?.message || 'Failed to load audit events');
        },
      });
  }

  onFilterChange(): void {
    this.page.set(1);
    this.reload();
  }

  onPageSizeChange(size: number | string): void {
    this.pageSize.set(Number(size) || 10);
    this.page.set(1);
    this.reload();
  }

  goPrev(): void {
    if (this.page() <= 1) return;
    this.page.update((p) => p - 1);
    this.reload();
  }

  goNext(): void {
    if (this.page() >= this.totalPages()) return;
    this.page.update((p) => p + 1);
    this.reload();
  }

  goFirst(): void {
    if (this.page() === 1) return;
    this.page.set(1);
    this.reload();
  }

  goLast(): void {
    if (this.page() >= this.totalPages()) return;
    this.page.set(this.totalPages());
    this.reload();
  }

  rangeLabel(): string {
    if (!this.total()) return '0 of 0';
    const start = (this.page() - 1) * this.pageSize() + 1;
    const end = Math.min(this.page() * this.pageSize(), this.total());
    return `${start}–${end} of ${this.total()}`;
  }

  /** Global serial number across pages (1 … total). */
  rowNumber(indexOnPage: number): number {
    return (this.page() - 1) * this.pageSize() + indexOnPage + 1;
  }
}
